import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';

interface RoleArchiveArgs {
  roleId: number;
  tenantId: number;
  organizationId: string;
}

/**
 * Archive (soft-delete) a role.
 *   POST /api/TenantRoles/{id}/Archive?TenantId={tid}&organizationId={oid}
 *
 * Bodiless action POST — Flutter sends the same shape
 * (`settings_data_source_impl.dart:742-751`). Listed under the cURL-pending
 * follow-up since I haven't confirmed the request/response shape against a
 * live cURL, but the read of the Flutter source is authoritative for the URL.
 */
export function useArchiveRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, tenantId, organizationId }: RoleArchiveArgs) => {
      const qs = new URLSearchParams({
        TenantId: String(tenantId),
        organizationId,
      });
      return post<unknown>(`/api/TenantRoles/${roleId}/Archive?${qs.toString()}`, undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles-list'] });
    },
  });
}

/**
 * Restore (un-archive) a role.
 *   POST /api/TenantRoles/{id}/Activate?TenantId={tid}&organizationId={oid}
 *
 * Same caveat as `useArchiveRole` — derived from Flutter source, pending cURL.
 */
export function useUnarchiveRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, tenantId, organizationId }: RoleArchiveArgs) => {
      const qs = new URLSearchParams({
        TenantId: String(tenantId),
        organizationId,
      });
      return post<unknown>(`/api/TenantRoles/${roleId}/Activate?${qs.toString()}`, undefined);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles-list'] });
    },
  });
}
